import { Box, Select, TabMenu, TabMenuItem } from "@bcc-ui/ui-components";
import * as React from "react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import {
    ALL_DEVICE_ITEMS,
    ALL_OS_ITEMS,
    ALL_PROCESSOR_ITEMS,
    DEVICE_OPERATIONAL_SYSTEMS,
    OS_PROCESSORS,
    USE_CASE_DEVICES,
} from "./filter-items";
import {
    BmpHomeOperatingSystem,
    BmpHomeProcessor,
    BmpHomeUseCase,
    BpmHomeTarget,
    SoftwarePackage,
} from "domain/softwarePackages";

import testIds from "testIds.json";

const ALL_FILTER_VALUE = "ALL" as const;

interface Props {
    mainProducts: Record<string, SoftwarePackage[]>;
    onFilterChange: (filtered: Record<string, SoftwarePackage[]>) => void;
}

export function filterProducts(
    products: Record<string, SoftwarePackage[]>,
    useCase?: BmpHomeUseCase,
    device?: BpmHomeTarget,
    os?: BmpHomeOperatingSystem,
    processor?: BmpHomeProcessor
): Record<string, SoftwarePackage[]> {
    if (!useCase && !device && !os && !processor) {
        return products;
    }

    const filtered: Record<string, SoftwarePackage[]> = {};

    Object.entries(products).forEach(([groupName, softwarePackages]) => {
        const filteredPackages = softwarePackages.filter((product) => {
            if (useCase && !product.bmpHomeUseCases?.includes(useCase)) {
                return false;
            }
            if (device && !product.bpmHomeTargets?.includes(device)) {
                return false;
            }
            if (os && !product.bmpHomeOperatingSystems?.includes(os)) {
                return false;
            }
            if (processor && !product.bmpHomeProcessors?.includes(processor)) {
                return false;
            }
            return true;
        });
        if (filteredPackages.length > 0) {
            filtered[groupName] = filteredPackages;
        }
    });

    return filtered;
}

export const HomeFilters = ({ mainProducts, onFilterChange }: Props) => {
    const { t } = useTranslation();

    const [activeTabIndex, setActiveTabIndex] = React.useState(0);
    const [selectedUseCase, setSelectedUseCase] = React.useState<BmpHomeUseCase | undefined>(undefined);
    const [selectedDevice, setSelectedDevice] = React.useState<BpmHomeTarget | undefined>(undefined);
    const [selectedOS, setSelectedOS] = React.useState<BmpHomeOperatingSystem | undefined>(undefined);
    const [selectedProcessor, setSelectedProcessor] = React.useState<BmpHomeProcessor | undefined>(undefined);

    useEffect(() => {
        onFilterChange(filterProducts(mainProducts, selectedUseCase, selectedDevice, selectedOS, selectedProcessor));
    }, [mainProducts, selectedUseCase, selectedDevice, selectedOS, selectedProcessor]);

    const tabItems = React.useMemo(
        () => [
            { label: t("Onboarding.home.filters.tabs.all"), useCase: undefined, testId: testIds.homePage.allTabItem },
            {
                label: t("Onboarding.home.filters.tabs.endpoint"),
                useCase: BmpHomeUseCase.ENDPOINT,
                testId: testIds.homePage.endpointTabItem,
            },
            {
                label: t("Onboarding.home.filters.tabs.dataCenter"),
                useCase: BmpHomeUseCase.DATA_CENTER,
                testId: testIds.homePage.dataCenterTabItem,
            },
            {
                label: t("Onboarding.home.filters.tabs.mobile"),
                useCase: BmpHomeUseCase.MOBILE,
                testId: testIds.homePage.mobileTabItem,
            },
        ],
        [t]
    );

    function handleTabChange(tabIndex: number, useCase?: BmpHomeUseCase) {
        if (activeTabIndex === tabIndex) {
            return;
        }
        setActiveTabIndex(tabIndex);
        setSelectedUseCase(useCase);
        setSelectedDevice(undefined);
        setSelectedOS(undefined);
        setSelectedProcessor(undefined);
    }

    function handleDeviceChange(value: BpmHomeTarget | typeof ALL_FILTER_VALUE) {
        const newValue = value === ALL_FILTER_VALUE ? undefined : value;
        if (newValue === selectedDevice) {
            return;
        }
        setSelectedDevice(newValue);
        setSelectedOS(undefined);
        setSelectedProcessor(undefined);
    }

    function handleOsChange(value: BmpHomeOperatingSystem | typeof ALL_FILTER_VALUE) {
        const newValue = value === ALL_FILTER_VALUE ? undefined : value;
        if (newValue === selectedOS) {
            return;
        }
        setSelectedOS(newValue);
        setSelectedProcessor(undefined);
    }

    function handleProcessorChange(value: BmpHomeProcessor | typeof ALL_FILTER_VALUE) {
        const newValue = value === ALL_FILTER_VALUE ? undefined : value;
        if (newValue === selectedProcessor) {
            return;
        }
        setSelectedProcessor(newValue);
    }

    const allProducts = React.useMemo(() => Object.values(mainProducts).flat(), [mainProducts]);

    const availableUseCases = React.useMemo(() => {
        const available = new Set<BmpHomeUseCase>();
        allProducts.forEach((product) => {
            product.bmpHomeUseCases?.forEach((useCase) => {
                available.add(useCase);
            });
        });
        return available;
    }, [allProducts]);

    const { availableDeviceItems, availableOsItems, availableProcessorItems } = React.useMemo(() => {
        const availableDevices = new Set<BpmHomeTarget>();
        const availableOses = new Set<BmpHomeOperatingSystem>();
        const availableProcessors = new Set<BmpHomeProcessor>();

        for (const product of allProducts) {
            const matchesUseCase = !selectedUseCase || product.bmpHomeUseCases?.includes(selectedUseCase);
            const matchesDevice = !selectedDevice || product.bpmHomeTargets?.includes(selectedDevice);
            const matchesOS = !selectedOS || product.bmpHomeOperatingSystems?.includes(selectedOS);

            if (matchesUseCase) {
                product.bpmHomeTargets?.forEach((target) => availableDevices.add(target));
            }
            if (matchesUseCase && matchesDevice) {
                product.bmpHomeOperatingSystems?.forEach((os) => availableOses.add(os));
            }
            if (matchesUseCase && matchesDevice && matchesOS) {
                product.bmpHomeProcessors?.forEach((proccessor) => availableProcessors.add(proccessor));
            }
        }

        const filteredDeviceItems = ALL_DEVICE_ITEMS.filter(
            (item) =>
                availableDevices.has(item.value) &&
                (selectedUseCase === undefined || USE_CASE_DEVICES[selectedUseCase].has(item.value))
        ).map((item) => ({ ...item, label: t(item.label) }));

        const filteredOsItems = ALL_OS_ITEMS.filter(
            (item) =>
                availableOses.has(item.value) &&
                (selectedDevice === undefined || DEVICE_OPERATIONAL_SYSTEMS[selectedDevice]?.has(item.value))
        );

        const filteredProcessorItems = ALL_PROCESSOR_ITEMS.filter(
            (item) =>
                availableProcessors.has(item.value) &&
                (selectedDevice === undefined || !!DEVICE_OPERATIONAL_SYSTEMS[selectedDevice]) &&
                (selectedOS === undefined || OS_PROCESSORS[selectedOS]?.has(item.value))
        );

        return {
            availableDeviceItems: [
                { value: ALL_FILTER_VALUE, label: t("Onboarding.home.filters.selects.devices") },
                ...filteredDeviceItems,
            ],
            availableOsItems: [
                { value: ALL_FILTER_VALUE, label: t("Onboarding.home.filters.selects.operatingSystems") },
                ...filteredOsItems,
            ],
            availableProcessorItems: [
                { value: ALL_FILTER_VALUE, label: t("Onboarding.home.filters.selects.processors") },
                ...filteredProcessorItems,
            ],
        };
    }, [allProducts, selectedUseCase, selectedDevice, selectedOS, t]);

    return (
        <Box columnLayout gap="Xs">
            <Box>
                <TabMenu>
                    {tabItems
                        .filter(({ useCase }) => !useCase || availableUseCases.has(useCase))
                        .map(({ label, useCase, testId }, index) => (
                            <TabMenuItem
                                key={index}
                                isActive={activeTabIndex === index}
                                onClick={() => handleTabChange(index, useCase)}
                                testId={testId}
                            >
                                {label}
                            </TabMenuItem>
                        ))}
                </TabMenu>
            </Box>
            <Box gap="Xs" horizontalAlignment="Right">
                <Select
                    key={`device-${selectedUseCase}`}
                    onSelection={handleDeviceChange}
                    hideLabelSection
                    name={"device"}
                    placeholder={t("Onboarding.home.filters.selects.devices")}
                    items={availableDeviceItems}
                    width={180}
                    disabled={availableDeviceItems.length <= 1 && !selectedDevice}
                    testId={testIds.homePage.devicesSelect}
                />
                <Select
                    key={`os-${selectedUseCase}-${selectedDevice}`}
                    onSelection={handleOsChange}
                    hideLabelSection
                    name={"os"}
                    placeholder={t("Onboarding.home.filters.selects.operatingSystems")}
                    items={availableOsItems}
                    width={180}
                    disabled={availableOsItems.length <= 1 && !selectedOS}
                    testId={testIds.homePage.operatingSystemsSelect}
                />
                <Select
                    key={`processor-${selectedUseCase}-${selectedDevice}-${selectedOS}`}
                    onSelection={handleProcessorChange}
                    hideLabelSection
                    name={"processor"}
                    placeholder={t("Onboarding.home.filters.selects.processors")}
                    items={availableProcessorItems}
                    width={180}
                    disabled={availableProcessorItems.length <= 1 && !selectedProcessor}
                    testId={testIds.homePage.processorsSelect}
                />
            </Box>
        </Box>
    );
};

export default HomeFilters;
